import { inject, Injectable } from '@angular/core';
import { AlertDialogComponent } from '../components/shared/alert-dialog/alert-dialog.component';
import {
  RdsDialogRef,
  RdsDialogService,
  RdsToastService,
} from '@rds/angular-components';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../components/shared/confirm-dialog/confirm-dialog.component';
import { LoadingDialogComponent } from '../components/shared/loading-dialog/loading-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class UiService {
  private readonly _dialog = inject(RdsDialogService);
  private readonly _router = inject(Router);
  private _loadingDialogRef: RdsDialogRef<LoadingDialogComponent> | null = null;
  private readonly _rdsToastService = inject(RdsToastService);

  showSuccessAlert(
    title: string,
    description: string,
    navigation?: string,
    buttonConfirm?: string,
    buttonClose?: string
  ) {
    this._dialog
      .open(AlertDialogComponent, {
        size: 's',
        data: {
          title: title,
          description: description,
          icon: 'circle_checkmark',
          iconColor: '#006D5E',
          navigation: navigation,
          buttonConfirm: buttonConfirm ?? 'Confirm',
          buttonClose: buttonClose ?? 'Close',
        },
      })
      .afterClosed()
      .subscribe();
  }

  showErrorAlert(
    title: string,
    description: string,
    navigation?: string,
    buttonConfirm?: string,
    buttonClose?: string
  ) {
    this._dialog
      .open(AlertDialogComponent, {
        size: 's',
        data: {
          title: title,
          description: description,
          icon: 'circle_close',
          iconColor: '#D32F2F',
          navigation: navigation,
          buttonConfirm: buttonConfirm ?? 'Confirm',
          buttonClose: buttonClose ?? 'Close',
        },
      })
      .afterClosed()
      .subscribe();
  }

  showWarningAlert(
    title: string,
    description: string,
    navigation?: string,
    buttonConfirm?: string,
    buttonClose?: string
  ) {
    this._dialog
      .open(AlertDialogComponent, {
        size: 's',
        data: {
          title: title,
          description: description,
          icon: 'circle_exclamation',
          iconColor: '#FFC107',
          navigation: navigation,
          buttonConfirm: buttonConfirm ?? 'Confirm',
          buttonClose: buttonClose ?? 'Close',
        },
      })
      .afterClosed()
      .subscribe();
  }

  showInfoAlert(
    title: string,
    description: string,
    navigation?: string,
    buttonConfirm?: string,
    buttonClose?: string
  ) {
    this._dialog
      .open(AlertDialogComponent, {
        size: 'm',
        data: {
          title: title,
          description: description,
          icon: 'circle_info',
          iconColor: '#1976D2',
          navigation: navigation,
          buttonConfirm: buttonConfirm ?? 'Confirm',
          buttonClose: buttonClose ?? 'Close',
        },
      })
      .afterClosed()
      .subscribe();
  }

  showConfirmDialog(
    title: string,
    message: string,
    confirmText?: string,
    cancelText?: string
  ) {
    return this._dialog.open(ConfirmDialogComponent, {
      size: 's',
      data: {
        title,
        message,
        icon: 'circle_info',
        iconColor: '#1976D2',
        confirmText,
        cancelText,
      },
    });
  }

  showDeleteDialog(
    title: string,
    message: string,
    warning?: string,
    confirmText?: string,
    cancelText?: string
  ) {
    return this._dialog.open(ConfirmDialogComponent, {
      size: 's',
      data: {
        title,
        message,
        warning,
        icon: 'circle_help',
        iconColor: '#FFC107',
        confirmText,
        cancelText,
      },
    });
  }

  showUnpublishedConfrimDialog(
    title: string,
    message: string,
    warning?: string,
    confirmText?: string,
    cancelText?: string
  ) {
    return this._dialog.open(ConfirmDialogComponent, {
      size: 's',
      data: {
        title,
        message,
        warning,
        icon: 'circle_help',
        iconColor: '#FFC107',
        confirmText,
        cancelText,
      },
    });
  }

  showLoading(message: string = 'Loading...', instruction: string = ''): void {
    if (this._loadingDialogRef) {
      return;
    }

    this._loadingDialogRef = this._dialog.open(LoadingDialogComponent, {
      size: 's',
      disableClose: true,
      data: {
        message,
        instruction,
      },
    });
  }

  hideLoading(): void {
    if (this._loadingDialogRef) {
      this._loadingDialogRef.close();
      this._loadingDialogRef = null;
    }
  }

  showSuccessToast(message: string, title: string): void {
    this._rdsToastService.success(message, title);
  }

  showErrorToast(title: string, message: string): void {
    this._rdsToastService.error(message, title);
  }
}
